import React from 'react'
import { Button, Tag, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { PiLockFill } from 'react-icons/pi'

import IntlMessages from '../../util/IntlMessages'

import { IAddonsData } from '@vacationtracker/shared/types/automations'
import { AddonsStatus, AddonsStatusEnum } from '@vacationtracker/shared/types/addons'
import { SubscriptionPlan, SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'

interface IAddonsCard extends IAddonsData {
  viewMode: boolean
  status: AddonsStatus
  onSelectAddon: (url: string, type: string, allowViewMode: boolean) => void
  isTrialSubscription: boolean
  authUserRole?: string
  helpDesklink?: string
  includedIn?: string[]
  subscriptionPlan: SubscriptionPlan
  allowViewMode: boolean
  allowSelectAddon?: boolean
}

const AddonsCard = ({
  viewMode,
  status,
  type,
  parent,
  parentLabel,
  roles,
  url,
  icon,
  description,
  onSelectAddon,
  authUserRole,
  helpDesklink,
  isTrialSubscription,
  includedIn,
  subscriptionPlan,
  allowViewMode,
  allowSelectAddon = false,
}: IAddonsCard) => {
  // authUserRole property is passed to the component only on the MyAddons page
  // ChoiceAutomations page doesn't have this property as user should be able to request any addon
  let canSelectAddon = allowSelectAddon || false
  // If addon is included in the current plan, the user can select it
  if (!allowSelectAddon && includedIn && includedIn.length > 0 && includedIn.includes(subscriptionPlan)) {
    canSelectAddon = authUserRole ? roles.includes(authUserRole) : true
  }

  const addonCardExtra = (url: string, type: string, subscriptionPlan: SubscriptionPlan, includedIn?: string[]) => {
    if (url === 'coming-soon') {
      return <Tag style={{ margin: 0 }}><IntlMessages id="app.comingSoon" /></Tag>
    }

    if (includedIn && includedIn.length > 0 && includedIn.includes(subscriptionPlan)) {
      return (
        <Button size="small" type="primary" onClick={() => { onSelectAddon(url, type, allowViewMode) }}>
          {url === 'adp' ? <IntlMessages id="signin.adpLoginTitle" /> : <IntlMessages id="addonsPaymentStatus.activateForFree" />}
        </Button>
      )
    }

    if (includedIn && includedIn.length > 0 && includedIn.includes(SubscriptionPlanEnum.complete) && includedIn.includes(SubscriptionPlanEnum.core)) {
      return (
        <Button size="small" disabled className="locked-addon-button">
          <PiLockFill /><IntlMessages id="addonsPaymentStatus.availableOnCoreAndCompletePlan" />
        </Button>
      )
    }

    return (
      <Button size="small" disabled className="locked-addon-button">
        <PiLockFill /><IntlMessages id="addonsPaymentStatus.availableOnCompletePlan" />
      </Button>
    )
  }

  const getAddonStatus = (status: AddonsStatus, subscriptionPlan: SubscriptionPlan, isTrialSubscription: boolean, includedIn?: string[]) => {
    if (
      includedIn
      && includedIn.length > 0
      && includedIn.includes(subscriptionPlan)
      // Even if the addon is included in the current plan, we need to check its status because displaying the "Included in your plan" label,
      // because users might be paying for this addon (status is "ACTIVE") from before our plan change, or they have the addon in the "CANCEL_AT_PERIOD_END" status
      // Only if the status is "GRANT_PERIOD" or "CANCELED", we can display the "Included in your plan" label
      && !([AddonsStatusEnum.ACTIVE, AddonsStatusEnum.CANCEL_AT_PERIOD_END] as AddonsStatus[]).includes(status)
    ) {
      return <span className="free"><IntlMessages id="choiceAutomations.includedInYourPlan" /></span>
    }
    if (isTrialSubscription) {
      return <span className="trial-period"><IntlMessages id="app.trialPeriod" /></span>
    }
    if (status === AddonsStatusEnum.ACTIVE) {
      return <span className="active"><IntlMessages id="addonsPaymentStatus.paymentActive" /></span>
    }
    if (status === AddonsStatusEnum.CANCEL_AT_PERIOD_END) {
      return <span className="cancel-at-period-end"><IntlMessages id="addonsPaymentStatus.activeUntilBillingPeriodEnds" /></span>
    }
    if (status === AddonsStatusEnum.GRANT_PERIOD) {
      return <span className="grant-period"><IntlMessages id="addonsPaymentStatus.Active" /></span>
    }
    if (status === AddonsStatusEnum.CANCELED) {
      return <span className="canceled"><IntlMessages id="addonsPaymentStatus.canceled" /></span>
    }
    return ''
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectAddon = (event: any, url: string, type: string, allowViewMode: boolean) => {
    if (event.target && event.target.id === 'helpdesk') {
      return
    }
    onSelectAddon(url, type, allowViewMode)
  }

  return (
    <div
      className={`addons-card ${canSelectAddon ? 'hoverable' : 'disabled'} ${parent} ${status}`}
      onClick={(event) => canSelectAddon && handleSelectAddon(event, url, type, allowViewMode)}
      key={type}
    >
      <div className="addons-card-top-header"><Tag>{parentLabel}</Tag></div>
      <div className="addons-card-header">
        <div className="addons-card-header-icon">
          {icon}
        </div>
        <div className="addons-card-header-extra">
          {(viewMode ? 
            <Button size="small" type="primary" onClick={() => { onSelectAddon(url, type, allowViewMode) }}>
              <IntlMessages id="app.view" />
            </Button> :
            addonCardExtra(url, type, subscriptionPlan, includedIn)
          )}
          {viewMode ? 
            getAddonStatus(status, subscriptionPlan, isTrialSubscription, includedIn) :
            <div>&nbsp;</div>
          }
          {roles.length > 0 &&
            <IntlMessages
              id="choiceAutomations.accessibleTo"
              values={{
                approver: (...chunks) => roles.includes('Approver') ? chunks : '',
                admin: (...chunks) => roles.includes('Admin') ? chunks : '',
                and: (...chunks) => roles.length === 2 ? chunks : '',
                only: (...chunks) => roles.length === 1 ? chunks : '',
              }}
            />
          }
        </div>
      </div>
      <div className="addons-card-title">
        <IntlMessages id={`automations.${type}`} />
        {type === 'ADP' && <Tag style={{ marginLeft: 8 }}>BETA</Tag>}
        {helpDesklink &&
          <Tooltip 
            title={
              <IntlMessages 
                id="automations.stepByStepHelpDesk" 
                values={{
                  automationName: () => <IntlMessages id={`automations.${type}`} />,
                  link: (...chunks) => <a href={helpDesklink} target="_blank" id="helpdesk" rel="noopener noreferrer">{chunks}</a>,
                }} 
              />
            }
          >
            <InfoCircleOutlined style={{ marginLeft: 10 }} />
          </Tooltip>
        }
      </div>
      <div className="addons-card-description">
        {description}
      </div>
    </div>
  )
}

export default AddonsCard
